/**
 * PatientRegistrationForm
 *
 * Registers a patient with NO name, and edits one — both from the SAME shape,
 * because a write is always the caller's complete view and `$defs.create` of
 * the tailored `$schema` document is that view.
 *
 * The controls are not written here. `GET /patients/$schema?location=<unit>`
 * answers with a JSON Schema 2020-12 document resolved for this caller at this
 * unit, and `@rjsf/core` renders it through the shadcn theme in
 * `rjsfShadcn.tsx`. So a ward that starts demanding `glasgow`, or narrows
 * `mobility` to two of its three values, changes this form on the next request
 * with nothing shipped — which is the whole point of a schema being a property
 * of (resource × profession × unit) rather than of a release.
 *
 * The unit is a DEDICATED field, never an attribute (the org-hierarchy
 * locations API — real units — never free text): registration OPENS the first
 * encounter (add = create + place), so the backend needs a real `location_id`.
 * `ward` is not a schema field for the same reason — the ward IS the placement
 * (`patients.location_id` plus the placement-event log), and a duplicated
 * `ward` attribute went stale after every transfer. Room and bed stay fields:
 * they are finer-grained than the placement leaf.
 *
 * Two write paths, one shape:
 *  - **register** — `POST /patients`, with the picked unit.
 *  - **edit** (`editToken`) — `PUT /patients/{id}` with `If-Match` carrying the
 *    entity tag of the read the body was computed from. A 412 means somebody
 *    else wrote in between: the form says so, re-reads, and the nurse re-applies
 *    her change on top of what is actually stored. The unit is not editable
 *    here; a move is a transfer, not an attribute edit.
 *
 * When the READ view carries an `admission_ref` field with
 * `strategy: 'minted'`, the nurse fills in its declared `link_fields` instead
 * of the token: the server mints the token. The field cannot appear as a
 * control at all, because a minted field is absent from `$defs.create` by
 * construction — the form needs no strategy check to leave it out, and reads
 * the strategy off `$defs.item` only to decide whether to ask for link fields.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import RJSFForm from '@rjsf/core';
import type { IChangeEvent } from '@rjsf/core';
import type { ObjectFieldTemplateProps, RJSFSchema, RJSFValidationError } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  useRegisterPatient,
  usePatientsConfig,
  usePatientsSchema,
  usePatientSchema,
  usePatientWithRevision,
  useUpdatePatientAttributes,
} from '@/hooks/usePatients';
import { useLocations } from '@/components/admin/org/useOrg';
import { useAuthStore } from '@/stores/auth';
import { localId } from '@/lib/reference';
import { OptionChips } from './OptionChips';
import {
  formatPatientLabel,
  formatPatientSecondary,
  humaniseAttributeKey,
} from '@/lib/patientLabel';
import {
  displayValue,
  readableFields,
  writableFields,
  type PatientSchemaDocument,
} from '@/api/patientSchema';
import { parsePatientAttributeViolations, type PatientResponse } from '@/api/endpoints/patients';
import { ApiError } from '@/api/client';
import {
  annotationOf,
  fieldLabelKey,
  omitEmpty,
  shadcnTemplates,
  shadcnWidgets,
} from './rjsfShadcn';

// Fallback only — the real list is config-driven (patients.ward_kinds); used when
// the config block omits it during rollout.
const DEFAULT_WARD_KINDS = ['ward', 'icu'];

/** Where the last-used ward is remembered between visits — the weakest of the
 *  three seeds below, and only ever a seed. An org location id, no PHI. */
const LAST_WARD_STORAGE_KEY = 'tricorder.patientRegistration.lastWardId';

function readLastUsedWard(): string | undefined {
  try {
    return window.localStorage.getItem(LAST_WARD_STORAGE_KEY) ?? undefined;
  } catch {
    // Private mode / disabled storage — a missing seed is not an error.
    return undefined;
  }
}

function rememberLastUsedWard(id: string | undefined): void {
  if (!id) return;
  try {
    window.localStorage.setItem(LAST_WARD_STORAGE_KEY, id);
  } catch {
    // Same: remembering is a convenience, never a requirement.
  }
}

/**
 * The schema `<Form>` renders: a reference into the served document's own
 * `$defs`, so rjsf resolves `create` from the SAME bytes the server validates
 * against. Nothing is copied out and nothing is rebuilt — the root of the
 * served document IS its `$defs`.
 */
function createShape(doc: PatientSchemaDocument | undefined): RJSFSchema | null {
  if (!doc?.$defs?.create) return null;
  return { $ref: '#/$defs/create', $defs: doc.$defs } as RJSFSchema;
}

/** What the disclosure below needs from the form, handed to the templates the
 *  rjsf way rather than through a module-level variable. */
interface RegistrationFormContext {
  showOptional: boolean;
  onToggleOptional: () => void;
  moreFieldsLabel: string;
}

/**
 * The attribute group, split by what the ward actually demands.
 *
 * Required fields stay on screen; everything else is one tap away. On a phone
 * this is the difference between a form that fits and a form the nurse scrolls
 * to find the button on. The split is read off the document's own `required`
 * array, so it follows the ward rather than a list here.
 *
 * The optional group is HIDDEN, not unmounted: a value typed into an optional
 * field and then collapsed away must still be submitted.
 */
function DisclosureObjectFieldTemplate(props: ObjectFieldTemplateProps) {
  const { properties, schema, registry } = props;
  // v6 hands `formContext` through the registry, not as a top-level prop.
  const context = registry.formContext as RegistrationFormContext;
  const required = new Set(schema.required ?? []);
  const requiredProperties = properties.filter((p) => required.has(p.name));
  const optionalProperties = properties.filter((p) => !required.has(p.name));

  return (
    <div className="space-y-5">
      {requiredProperties.length > 0 && (
        <div className="space-y-4">
          {requiredProperties.map((p) => (
            <div key={p.name}>{p.content}</div>
          ))}
        </div>
      )}
      {optionalProperties.length > 0 && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={context.onToggleOptional}
            aria-expanded={context.showOptional}
            aria-controls="register-optional"
            data-testid="register-optional-toggle"
            className="inline-flex min-h-11 items-center gap-1.5 text-[15px] font-semibold text-primary transition active:scale-95"
          >
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', context.showOptional && 'rotate-180')}
              aria-hidden="true"
            />
            {context.moreFieldsLabel}
          </button>
          <div id="register-optional" hidden={!context.showOptional} className="space-y-4">
            {optionalProperties.map((p) => (
              <div key={p.name}>{p.content}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const templates = { ...shadcnTemplates, ObjectFieldTemplate: DisclosureObjectFieldTemplate };

interface PatientRegistrationFormProps {
  /**
   * A patient was registered. The form STAYS OPEN and ready for the next one —
   * this is a notification, not a hand-off, and a caller must not navigate away
   * on it. Use `onDone` for the exit.
   */
  onRegistered?: (patient: PatientResponse) => void;
  /** The nurse pressed "Fertig" — the only way out of the entry loop. When
   *  omitted no exit affordance renders (the DEV preview has nowhere to go). */
  onDone?: () => void;
  /**
   * The ward the nurse is currently looking at, if the surface can tell —
   * the STRONGEST unit seed, ahead of her profile unit and the last one she
   * used. Registering someone almost always means registering them where she
   * already is.
   */
  contextLocationId?: string | null;
  /**
   * Edit this patient instead of registering a new one. The shape comes from
   * the patient's OWN placement (`GET /patients/{id}/$schema`), so a transfer
   * changes the form with nothing on the record changing.
   */
  editToken?: string;
  /** An edit was saved. */
  onSaved?: (patient: PatientResponse) => void;
}

export function PatientRegistrationForm({
  onRegistered,
  onDone,
  contextLocationId,
  editToken,
  onSaved,
}: PatientRegistrationFormProps) {
  const { t } = useTranslation();
  const config = usePatientsConfig();
  const register = useRegisterPatient();
  const update = useUpdatePatientAttributes();
  const editing = !!editToken;

  // The unit is picked from the org-hierarchy locations API (real units), not
  // typed free-hand. GET /locations is the org vocabulary (public listing), so
  // any nurse can resolve the unit list. There is no free-text fallback: a
  // patient's attributes must never carry a dead, unvalidated "ward" string —
  // if the list can't be resolved the nurse is told a unit is required and the
  // form blocks submission, rather than silently writing that dead attribute.
  const { data: locations, isLoading: locationsLoading } = useLocations();
  const wardKinds = useMemo(() => {
    const kinds = config?.ward_kinds?.length ? config.ward_kinds : DEFAULT_WARD_KINDS;
    return new Set(kinds);
  }, [config?.ward_kinds]);
  const wardOptions = useMemo(
    () => (locations ?? []).filter((l) => wardKinds.has(l.kind)),
    [locations, wardKinds],
  );
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [wardLocationId, setWardLocationId] = useState<string | undefined>(undefined);
  // Once the nurse picks a unit, her choice wins — the profile seed below must
  // never override an explicit pick.
  const [unitTouched, setUnitTouched] = useState(false);
  const selectedWard = wardOptions.find((w) => w.id === wardLocationId);

  // The unit seed, strongest first: the ward she is LOOKING AT, then her own
  // profile unit (user.locationRef, set during onboarding self-assign — the
  // same profile-unit-leads precedence as useUnitScope in useAccess.ts), then
  // the last ward she registered into. The point is that the common case costs
  // ZERO interactions: a nurse registering at the bedside is registering onto
  // the ward already on her screen.
  const me = useAuthStore((s) => s.user);
  const profileUnitId = localId(me?.locationRef, 'Location') ?? undefined;
  const seedWardId = useMemo(() => {
    const selectable = (id: string | undefined | null): id is string =>
      !!id && wardOptions.some((w) => w.id === id);
    if (selectable(contextLocationId)) return contextLocationId;
    if (selectable(profileUnitId)) return profileUnitId;
    const remembered = readLastUsedWard();
    return selectable(remembered) ? remembered : undefined;
  }, [contextLocationId, profileUnitId, wardOptions]);
  useEffect(() => {
    if (editing || unitTouched || !seedWardId) return;
    setWardLocationId((prev) => prev ?? seedWardId);
  }, [editing, unitTouched, seedWardId]);

  // There is no "the picker is unavailable, so refuse the submit" check, and
  // there is nothing left for one to catch. Without a unit there is no
  // document, without a document there is no `<form>`, and without a `<form>`
  // there is no submit button — a failed fetch and an empty `ward_kinds`
  // vocabulary both land on the picker's own "could not load the unit list"
  // line below. A guard that cannot fire reads as coverage it does not have.
  //
  // The shape: from the patient's own placement when editing, from the picked
  // unit when registering. One document either way, and one renderer.
  const unitSchema = usePatientsSchema(editing ? null : wardLocationId);
  const patientSchema = usePatientSchema(editToken ?? null);
  const schemaDoc = editing ? patientSchema.data : unitSchema.data;
  const schemaLoading = editing ? patientSchema.isLoading : unitSchema.isLoading;
  const formSchema = useMemo(() => createShape(schemaDoc), [schemaDoc]);

  // The read an edit body is computed from, and the entity tag that read
  // carried. Both come from one query so the tag can never describe a
  // different revision than the values on screen.
  const revision = usePatientWithRevision(editToken ?? null);
  const editEtag = revision.data?.etag ?? null;

  // admission_ref pseudonymisation (design §2): read off the READ view, not the
  // write view — a minted field is absent from `$defs.create` by construction,
  // so looking for it there would always come up empty and the link-field group
  // would never render.
  const mintedField = useMemo(
    () => readableFields(schemaDoc).find((f) => f.strategy === 'minted'),
    [schemaDoc],
  );
  const linkFieldKeys = mintedField?.link_fields ?? [];
  const [admissionLink, setAdmissionLink] = useState<Record<string, string>>({});
  // Set once registration under `minted` succeeds — holds the form on a
  // read-only "here is the token" screen instead of immediately closing
  // (the UI IS part of the no-PHI-in-free-text control, design §2). A
  // `passthrough` registration never sets this and closes exactly as before.
  const [justMinted, setJustMinted] = useState<{ token: string; patient: PatientResponse } | null>(
    null,
  );

  // The entry LOOP. Shift-start census entry is ten patients in a row, so a
  // save must behave like a checkout scanner — confirm and move on — not like
  // a form that navigates away and has to be reopened per patient.
  const [batch, setBatch] = useState<{ count: number; label: string; placement: string } | null>(
    null,
  );
  // Optional attributes are collapsed by default so the common case is unit +
  // the ward's required fields + submit. Open/closed is batch-stable: a nurse
  // who needed the extra fields for patient 1 needs them for patient 2.
  const [showOptional, setShowOptional] = useState(false);
  // Every reason the last submit attempt did not go through, named. The button
  // itself stays pressable: a disabled control that will not say why it is
  // disabled was the defect this replaces.
  const [blockingReasons, setBlockingReasons] = useState<string[]>([]);
  // Set when a save lost a race. It is not an error the nurse caused, so it
  // reads as what happened and what the form did about it.
  const [staleWrite, setStaleWrite] = useState(false);

  const writableKeys = useMemo(
    () => writableFields(schemaDoc).map((f) => f.key),
    [schemaDoc],
  );

  // A value typed for a field that only exists on the PREVIOUSLY selected
  // unit's schema must not survive a unit switch — it would submit as an
  // invisible, unvalidated key. `omitExtraData` drops it at submit; pruning
  // here also takes it off the screen. A key present in both schemas (room/bed
  // are shared across units) keeps its value.
  useEffect(() => {
    if (writableKeys.length === 0) return;
    const allowed = new Set(writableKeys);
    setFormData((prev) => {
      const pruned: Record<string, unknown> = {};
      let changed = false;
      for (const [k, v] of Object.entries(prev)) {
        if (allowed.has(k)) pruned[k] = v;
        else changed = true;
      }
      return changed ? pruned : prev;
    });
  }, [writableKeys]);

  // Same anti-stray-value pruning for the admission link group: a case number
  // typed for one minted ward's link_fields must not ride along into a
  // different unit's differently-declared ones.
  useEffect(() => {
    const allowed = new Set(mintedField?.link_fields ?? []);
    setAdmissionLink((prev) => {
      const pruned: Record<string, string> = {};
      let changed = false;
      for (const [k, v] of Object.entries(prev)) {
        if (allowed.has(k)) pruned[k] = v;
        else changed = true;
      }
      return changed ? pruned : prev;
    });
  }, [mintedField]);

  // Editing starts from what the record actually holds, for the WRITABLE fields
  // only. A field the record does not hold starts empty, and leaving it empty
  // means "not recorded" on save. Re-seeded whenever the read changes, which is
  // what makes the 412 path land the server's values in front of the nurse.
  const editedPatient = revision.data?.patient;
  useEffect(() => {
    if (!editedPatient || writableKeys.length === 0) return;
    setFormData(
      Object.fromEntries(
        writableKeys
          .filter((k) => editedPatient.attributes[k] !== undefined)
          .map((k) => [k, editedPatient.attributes[k]]),
      ),
    );
  }, [editedPatient, writableKeys]);

  const labelPreview = useMemo(
    () => formatPatientLabel(formData, config?.label_format, undefined, selectedWard?.name),
    [formData, config?.label_format, selectedWard?.name],
  );

  const pending = editing ? update.isPending : register.isPending;
  const mutationError = editing ? update.error : register.error;
  const violations =
    mutationError instanceof ApiError && mutationError.status === 422
      ? parsePatientAttributeViolations(mutationError.extensions)
      : [];

  /** An rjsf validation error, said the way the ward would say it: the field's
   *  own label, not a JSON pointer. */
  const describeError = (error: RJSFValidationError): string => {
    const key = (error.property ?? '').replace(/^\./, '') || error.params?.missingProperty;
    if (!key) return error.stack;
    const property = schemaDoc?.$defs?.create?.properties?.[key];
    const label = t(
      fieldLabelKey(key, annotationOf(property as RJSFSchema | undefined)),
      humaniseAttributeKey(key),
    );
    return `${label}: ${error.message ?? error.name}`;
  };

  const focusFirstField = () => {
    const first = writableKeys[0];
    if (first) document.getElementById(`root_${first}`)?.focus();
  };

  /** Record the receipt and reset for the next entry. */
  const noteRegistered = (created: PatientResponse) => {
    setBatch((prev) => ({
      count: (prev?.count ?? 0) + 1,
      label: formatPatientLabel(created.attributes, config?.label_format, created.token),
      placement: formatPatientSecondary(
        created.attributes,
        config?.label_format,
        selectedWard?.name,
      ),
    }));
    focusFirstField();
  };

  const handleChange = (event: IChangeEvent<Record<string, unknown>>) => {
    setFormData(event.formData ?? {});
  };

  const handleError = (errors: RJSFValidationError[]) => {
    setBlockingReasons(errors.map(describeError));
  };

  const handleSubmit = (event: IChangeEvent<Record<string, unknown>>) => {
    if (pending) return;
    // `omitExtraData` has already dropped anything the shape does not name;
    // `omitEmpty` drops what the nurse left blank, so an untouched optional
    // field is ABSENT from the body rather than an empty string the floor would
    // reject — and, on a PUT, absent is how the record sheds it.
    const attributes = omitEmpty(event.formData);

    const reasons: string[] = [];
    // At least one attribute must carry a value on registration — otherwise
    // there is nothing to identify the patient by and no label to render. A
    // minted field always satisfies this on its own (the server mints
    // identifying content), so a ward whose only field is admission_ref can
    // still register. An EDIT has no such floor: clearing a field is a
    // legitimate correction.
    if (!editing && Object.keys(attributes).length === 0 && !mintedField) {
      reasons.push(t('patients.register.needAnyAttribute'));
      setShowOptional(true);
    }
    if (reasons.length > 0) {
      setBlockingReasons(reasons);
      return;
    }
    setBlockingReasons([]);
    setStaleWrite(false);

    if (editing) {
      if (!editEtag) {
        setBlockingReasons([t('patients.register.revisionUnavailable')]);
        return;
      }
      update.mutate(
        { token: editToken as string, attributes, etag: editEtag },
        {
          onSuccess: (saved) => onSaved?.(saved),
          onError: (err) => {
            // Somebody else wrote between the read this body was computed from
            // and this save. The hook has already invalidated the read, so the
            // effect above re-seeds the form from what is actually stored; all
            // that is left is to say so, because a form that silently changed
            // under the nurse would be worse than the conflict.
            if (err instanceof ApiError && err.status === 412) {
              setStaleWrite(true);
              void revision.refetch();
            }
          },
        },
      );
      return;
    }

    const trimmedAdmissionLink: Record<string, string> = {};
    if (mintedField) {
      for (const key of linkFieldKeys) {
        const trimmed = (admissionLink[key] ?? '').trim();
        if (trimmed) trimmedAdmissionLink[key] = trimmed;
      }
    }

    register.mutate(
      {
        attributes,
        ...(Object.keys(trimmedAdmissionLink).length > 0
          ? { admission_link: trimmedAdmissionLink }
          : {}),
        ...(wardLocationId ? { location_id: wardLocationId } : {}),
      },
      {
        onSuccess: (created) => {
          setFormData({});
          setAdmissionLink({});
          // The unit deliberately DOES NOT reset: back-to-back registrations
          // happen on the ward the nurse is actually working — which may be an
          // explicitly-picked ward that differs from her profile unit.
          // `created.token` (the patient's own identity token) is a DIFFERENT
          // value from the minted admission_ref — never fall back to it here.
          const mintedToken = mintedField ? displayValue(created.attributes[mintedField.key]) : '';
          rememberLastUsedWard(wardLocationId);
          if (mintedToken) {
            setJustMinted({ token: mintedToken, patient: created });
          } else {
            noteRegistered(created);
            onRegistered?.(created);
          }
        },
      },
    );
  };

  const dismissJustMinted = () => {
    const patient = justMinted?.patient;
    setJustMinted(null);
    if (patient) {
      // Back into the loop, not out of it — acknowledging the token is a step
      // inside the entry flow, not the end of it.
      noteRegistered(patient);
      onRegistered?.(patient);
    }
  };

  const formContext: RegistrationFormContext = {
    showOptional,
    onToggleOptional: () => setShowOptional((open) => !open),
    moreFieldsLabel: t('patients.register.moreFields'),
  };

  // Config not yet loaded, or backend omits the patients block.
  if (!config) {
    return (
      <p className="text-[15px] text-neutral-500" role="status">
        {t('patients.register.configUnavailable')}
      </p>
    );
  }

  // Post-mint success screen — replaces the form until the nurse
  // acknowledges the token (design §2: the UI IS part of the no-PHI-in-
  // free-text control). Never reached on a `passthrough` schema.
  if (justMinted) {
    return (
      <div className="space-y-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
        <p className="text-[13px] font-semibold uppercase tracking-wide text-neutral-500">
          {t('patients.register.admissionLink.tokenLabel')}
        </p>
        <p className="font-mono text-[21px] font-semibold tracking-wide text-neutral-900">
          {justMinted.token}
        </p>
        <p className="text-[13px] leading-relaxed text-neutral-500">
          {t('patients.register.admissionLink.tokenExplanation')}
        </p>
        <Button
          type="button"
          size="pill"
          onClick={dismissJustMinted}
          className={cn('min-h-[44px] w-full text-[17px]')}
        >
          {t('patients.register.admissionLink.continue')}
        </Button>
      </div>
    );
  }

  const chrome = (
    <>
      {/* Live label preview — how this patient will read on the census. It
          belongs beside the fields that shape it, not above the whole form
          where it pushed the first input off a phone screen. */}
      {!editing && (
        <div className="rounded-[18px] border border-neutral-200 bg-neutral-50 p-4">
          <p className="text-[13px] font-semibold uppercase tracking-wide text-neutral-500">
            {t('patients.register.labelPreview')}
          </p>
          <p className="mt-1 text-[17px] font-semibold text-neutral-900">{labelPreview}</p>
        </div>
      )}

      {/* admission_ref link fields — the real KIS identifiers for a `minted`
          field (design §2/§4). Write-only: sent as `admission_link`, never
          displayed back. The server mints the token from these; the nurse
          never types the token itself. */}
      {!editing && mintedField && (
        <div className="space-y-4 rounded-lg border border-neutral-200 p-4">
          <div>
            <h3 className="font-display text-[15px] font-semibold tracking-tight text-neutral-900">
              {t('patients.register.admissionLink.title')}
            </h3>
            <p className="mt-1 text-[13px] leading-relaxed text-neutral-500">
              {t('patients.register.admissionLink.explanation')}
            </p>
          </div>
          {linkFieldKeys.map((key) => (
            <div key={key} className="space-y-1.5">
              <label
                htmlFor={`link-${key}`}
                className="block text-[15px] font-medium text-neutral-900"
              >
                {t(`patients.admissionLink.fields.${key}`, humaniseAttributeKey(key))}
              </label>
              <Input
                id={`link-${key}`}
                name={`link-${key}`}
                value={admissionLink[key] ?? ''}
                onChange={(e) =>
                  setAdmissionLink((prev) => ({ ...prev, [key]: e.target.value }))
                }
                autoComplete="off"
                className="min-h-[44px]"
              />
            </div>
          ))}
        </div>
      )}

      {staleWrite && (
        <p role="alert" data-testid="register-stale" className="text-[13px] text-destructive">
          {t('patients.register.staleWrite')}
        </p>
      )}

      {violations.map((v) => (
        <p key={`${v.field}-${v.code}`} className="text-[13px] text-destructive">
          {/* detail is optional on the wire; the field and the code are not. */}
          {v.detail || `${v.field}: ${v.code}`}
        </p>
      ))}

      {/* The action bar sticks to the bottom of the scrolling pane so the button
          is reachable without scrolling on a phone, however many fields the
          ward's schema carries. */}
      <div className="sticky bottom-0 space-y-3 border-t border-neutral-200 bg-white/90 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur">
        {blockingReasons.length > 0 && (
          <div
            role="alert"
            data-testid="register-blockers"
            className="rounded-[8px] border border-destructive/30 bg-destructive/5 p-3"
          >
            <ul className="space-y-1">
              {blockingReasons.map((reason) => (
                <li key={reason} className="text-[13px] text-destructive">
                  {reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            type="submit"
            size="pill"
            // Pressable even when incomplete: pressing it is how the nurse finds
            // out what is missing. Only a request in flight disables it.
            disabled={pending}
            className={cn('min-h-[44px] flex-1 text-[17px]')}
          >
            {pending
              ? t('patients.register.submitting')
              : editing
                ? t('patients.register.save')
                : batch
                  ? t('patients.register.submitNext')
                  : t('patients.register.submit')}
          </Button>
          {onDone && (
            <Button
              type="button"
              size="pill"
              variant="outline"
              onClick={onDone}
              data-testid="register-done"
              className="min-h-[44px] px-5 text-[15px]"
            >
              {t('patients.register.done')}
            </Button>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="space-y-5">
      {/* The receipt: who just went through, and how many this sitting. The
          scanner's beep — it confirms without interrupting. */}
      {batch && (
        <div
          role="status"
          data-testid="register-receipt"
          className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-[18px] border border-tone-ok/30 bg-tone-ok-tint px-4 py-2.5"
        >
          <Check className="h-4 w-4 shrink-0 self-center text-tone-ok" aria-hidden="true" />
          <span className="text-[15px] font-semibold text-neutral-900">
            {t('patients.register.justRegistered', { patient: batch.label })}
          </span>
          {batch.placement && (
            <span className="text-[13px] text-neutral-600">{batch.placement}</span>
          )}
          {batch.count > 1 && (
            <span className="ml-auto text-[13px] font-semibold tabular-nums text-neutral-600">
              {t('patients.register.batchCount', { count: batch.count })}
            </span>
          )}
        </div>
      )}

      {/* Dedicated unit field — the ward is PLACEMENT, never an attribute. The
          chips carry their own group label, so the visible text is a <span>: a
          <label htmlFor> would dangle whenever the chip path renders. Absent
          when editing: a move is a transfer, not an attribute edit. */}
      {!editing && (
        <div className="space-y-1.5">
          <span className="block text-[15px] font-medium text-neutral-900">
            {t('patients.attributes.unit', 'Unit')}
          </span>
          {wardOptions.length > 0 ? (
            <OptionChips
              id="patient-ward"
              legend={t('patients.attributes.unit', 'Unit')}
              placeholder={t('patients.register.wardPlaceholder', 'Select a unit')}
              options={wardOptions.map((w) => ({ value: w.id, label: w.name }))}
              value={wardLocationId ?? ''}
              onChange={(next) => {
                setUnitTouched(true);
                setWardLocationId(next || undefined);
              }}
            />
          ) : locationsLoading ? (
            <p className="text-[13px] text-neutral-500" role="status">
              {t('common.loading')}
            </p>
          ) : (
            <p className="text-[13px] text-neutral-500" role="status">
              {t(
                'patients.register.wardLoadError',
                'Could not load the unit list — a unit is required to register a patient.',
              )}
            </p>
          )}
        </div>
      )}

      {formSchema ? (
        <RJSFForm
          schema={formSchema}
          validator={validator}
          formData={formData}
          formContext={formContext}
          templates={templates}
          widgets={shadcnWidgets}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onError={handleError}
          // Anything the shape does not name is dropped at submit, so a value
          // left over from a unit switch can never reach the wire.
          omitExtraData
          liveOmit
          // The nurse finds out what is missing by pressing the button; going
          // red as she first tabs into a field is scolding, not helping.
          liveValidate={false}
          // The blockers list above is this form's error surface; rjsf's own
          // list would be a second, differently-worded copy of it.
          showErrorList={false}
          focusOnFirstError
          noHtml5Validate
          autoComplete="off"
          className="space-y-5"
        >
          {chrome}
        </RJSFForm>
      ) : (
        <p className="text-[13px] text-neutral-500" role="status">
          {schemaLoading
            ? t('common.loading')
            : editing
              ? t('patients.register.schemaUnavailable')
              : t('patients.register.pickUnitFirst')}
        </p>
      )}
    </div>
  );
}
